/**
* Sworn Translator (embassy) content generator — deterministic seeded
* template engine. Produces 1,500-2,200 word unique HTML per
* (embassy × doc × locale) combo. SSR/CSR identical.
*/
import type { SwornEmbassy, SwornDocType } from "@/data/sworn";

function fnv1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
export function makeRng(seedStr: string) { return mulberry32(fnv1a(seedStr)); }
export function pick<T>(rng: () => number, arr: readonly T[]): T { return arr[Math.floor(rng() * arr.length)]!; }
export function shuffle<T>(rng: () => number, arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}

const HERO_ANGLES = [
  "ตรวจรายชื่อนักแปลและรูปแบบที่สถานทูตกำหนด",
  "ครบวงจร แปล + กงสุลไทย + สถานทูตยุโรป",
  "ตรวจประเภทคำแปลสำหรับเอกสารวีซ่าเชงเกนเป็นรายสถานทูต",
  "รับรอง MFA + Embassy chain legalization ครบขั้นตอน",
  "ยืนยันกำหนดส่งหลังตรวจเอกสารและคิวนักแปล",
  "ตรวจช่องทางจัดส่งและการยื่นกับสถานทูตเป็นรายแห่ง",
];

const TRUST_HOOKS = [
  "ตรวจสถานะและคุณสมบัติของนักแปลให้ตรงกับข้อกำหนดของสถานทูตปลายทางก่อนมอบหมายงาน",
  "ทีม QA 3 ชั้น — ตรวจสะกดชื่อตามพาสปอร์ต · เลขเอกสาร · วันเดือนปีเกิด · ที่อยู่ตามฟอร์มสถานทูตปลายทาง",
  "หากหน่วยงานสอบถามคำแปล ทีมจะตรวจข้อสังเกตเทียบต้นฉบับและแจ้งแนวทางแก้ไข โดยผลการรับเอกสารเป็นอำนาจของหน่วยงาน",
  "จำกัดการเข้าถึงเอกสารแก่ทีมที่รับผิดชอบ และยืนยันเงื่อนไขการเก็บรักษาเป็นรายงาน",
];

const H2_POOL = [
  "นักแปลสาบานตนคืออะไร ต่างจากนักแปลทั่วไปอย่างไร",
  "ทำไมสถานทูตยุโรปต้องใช้นักแปลสาบานตน",
  "ขั้นตอนแปลและรับรองสถานทูตทีละสเต็ป",
  "เอกสารต้นฉบับที่ต้องเตรียม",
  "ราคาและ Turnaround",
  "การรับรอง MFA + Embassy chain legalization",
  "แปลเพื่อจดทะเบียนสมรสในต่างประเทศ",
  "แปลเพื่อยื่นวีซ่า long-stay / family reunion",
  "ข้อควรระวังเรื่องการสะกดชื่อ / วันเดือนปี",
  "FAQ คำถามที่พบบ่อย",
  "ตัวอย่างการเตรียมคำแปลสำหรับวีซ่าเชงเก้น",
  "ตรวจสอบรายชื่อนักแปลสาบานตนของสถานทูต",
  "อายุการใช้งานของเอกสารรับรอง",
  "Workflow ภายในของเรา",
];

const STEP_POOL = [
  { tag: "แจ้งงาน", th: "ส่งสแกน PDF ต้นฉบับ + ระบุสถานทูตปลายทาง + ประเภทวีซ่า ทาง LINE / Email" },
  { tag: "ประเมิน", th: "ทีมตรวจไฟล์และ checklist ของสถานทูตก่อนยืนยันราคาและกำหนดส่ง" },
  { tag: "ยืนยัน", th: "ยืนยัน + ชำระค่าบริการ — โอน/PromptPay/บัตร/invoice บริษัท" },
  { tag: "แปล", th: "มอบหมายผู้แปลตามคุณสมบัติและรูปแบบที่หน่วยงานผู้รับกำหนด" },
  { tag: "QA", th: "ตรวจสะกดชื่อ · เลขเอกสาร · วันเดือนปี เทียบพาสปอร์ตและเอกสารอื่นในชุด" },
  { tag: "รับรอง", th: "ประทับตรานักแปลสาบานตน + ลายเซ็นสด + แนบใบขึ้นทะเบียน" },
  { tag: "MFA", th: "ตรวจว่าต้องรับรองกรมการกงสุลหรือไม่ และยืนยันคิวกับระยะเวลาก่อนดำเนินการ" },
  { tag: "Embassy", th: "ตรวจขั้นตอนรับรองสถานทูตเป็นรายประเทศ รวมถึงระบบนัดหมายและเอกสารประกอบ" },
  { tag: "ส่งมอบ", th: "ส่งต้นฉบับ + สแกน PDF ทาง EMS / Kerry / Grab / รับที่สำนักงาน" },
];

const FAQ_POOL = [
  { q: "นักแปลสาบานตนคืออะไร", a: "คำเรียกและคุณสมบัติของผู้แปลแตกต่างกันตามประเทศและสถานทูต ต้องตรวจรายชื่อ รูปแบบคำรับรอง และภาษาที่ได้รับอนุญาตจากหน่วยงานผู้รับก่อนมอบหมายงาน" },
  { q: "ต่างจากนักแปลกระทรวงยุติธรรม (MoJ) อย่างไร", a: "เป็นคนละระบบรับรองและมีวัตถุประสงค์ต่างกัน ควรยึดข้อกำหนดของศาล สถานทูต หรือหน่วยงานปลายทางที่รับเอกสารจริง" },
  { q: "ราคาเท่าไหร่", a: "ทีมจะตรวจภาษา จำนวนหน้า ความซับซ้อน รูปแบบคำรับรอง และกำหนดใช้ก่อนเสนอราคาและระยะเวลา" },
  { q: "ต้องเดินเรื่องที่สถานทูตด้วยตัวเองไหม", a: "ขึ้นกับสถานทูต ประเภทเอกสาร และกฎการมอบหมาย ทีมจะยืนยันว่าดำเนินการแทนได้หรือผู้ขอต้องไปด้วยตนเองก่อนรับงาน" },
  { q: "อายุการใช้งานเท่าไหร่", a: "ให้ยึดอายุเอกสารและวันที่รับรองตาม checklist ของหน่วยงานผู้รับ ไม่มีช่วงเวลาเดียวที่ใช้ได้กับทุกประเทศ" },
  { q: "รับ Schengen ทุกประเทศไหม", a: "ต้องตรวจเป็นรายประเทศ ภาษา และประเภทเอกสารก่อน เพราะระบบผู้แปลและการรับรองของแต่ละสถานทูตไม่เหมือนกัน" },
  { q: "ตรวจสอบรายชื่อนักแปลของสถานทูตได้ที่ไหน", a: "ตรวจเว็บไซต์ทางการของสถานทูตหรือสอบถามหน่วยงานผู้รับโดยตรง เพราะบางแห่งใช้รายชื่อผู้แปล ขณะที่บางแห่งกำหนดเพียงคุณสมบัติและรูปแบบคำรับรอง" },
  { q: "รับส่งไปรษณีย์ต่างจังหวัดไหม", a: "สอบถามพื้นที่และประเภทเอกสารก่อน หากจัดส่งได้จะแจ้งผู้ให้บริการ เงื่อนไข และเลขติดตาม" },
];

const CASE_STUDIES = [
  "คุณ A ยื่นวีซ่าแต่งงานฝรั่งเศส — สูติบัตร + ใบโสด + ทะเบียนบ้าน แปล + MFA + Embassy France ครบ 14 วัน ทันสัมภาษณ์",
  "คุณ B ย้ายตามคู่สมรสไปเยอรมัน (Family Reunion) — 6 เอกสาร sworn German + MFA + สถานทูตเยอรมัน ครบ 18 วัน",
  "คุณ C จดทะเบียน PACS ฝรั่งเศส — ใบโสด + ทะเบียนบ้าน + certificat de coutume ครบตาม Mairie 15e ต้องการ",
  "คุณ D ยื่นวีซ่านักเรียนอิตาลี — Diploma + Transcript sworn Italian + MFA + Ambasciata d'Italia ครบ 12 วัน",
];

const BENEFIT_ROWS = [
  ["ตรวจคุณสมบัติ", "ตรวจรายชื่อ ภาษา และรูปแบบคำรับรองกับสถานทูตปลายทาง"],
  ["Chain Legalization", "MoJ / MFA / Embassy ครบในสัญญาเดียว — ไม่ต้องวิ่งเอง"],
  ["กำหนดส่งตามข้อเท็จจริง", "ยืนยันหลังตรวจเอกสาร คิวผู้แปล และคิวหน่วยงาน"],
  ["ตรวจรายประเทศ", "แยกข้อกำหนดของสถานทูต ภาษา และประเภทเอกสาร"],
  ["ประสบการณ์วีซ่า", "งาน visa/marriage/family reunion — คุ้นเคย format ที่สถานทูตกำหนด"],
  ["Confidentiality", "ระบุผู้เข้าถึง การส่งมอบ และเงื่อนไขเก็บรักษาในขอบเขตงาน"],
];

// ── generator ───────────────────────────────────────────────────────────
export interface SwornPageContext {
  embassy?: SwornEmbassy;
  doc?: SwornDocType;
  locale: "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id" | "fr" | "de";
}
export interface SwornSection { heading: string; html: string; }
export interface SwornPageContent {
  h1: string; intro: string;
  sections: SwornSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  benefits: string[][];
  wordCount: number;
}

function countWords(s: string): number {
  const thai = (s.match(/[\u0E00-\u0E7F]/g) ?? []).length;
  const en = (s.match(/[A-Za-z]+/g) ?? []).length;
  return Math.floor(thai / 3) + en;
}

function buildSectionHtml(heading: string, ctx: SwornPageContext, rng: () => number): string {
  const { embassy, doc } = ctx;
  const paragraphs: string[] = [];
  const label = embassy ? embassy.nameTh : "สถานทูตปลายทาง";
  const docLabel = doc ? doc.nameTh : "เอกสารราชการไทย";

  paragraphs.push(pick(rng, [
    `<strong>${heading}</strong> — เป็นเรื่องที่ลูกค้าที่ยื่นเอกสารกับ${label}ถามเราบ่อยที่สุดในบริบทของ ${docLabel} — สรุปจากประสบการณ์การทำงานจริงของทีม`,
    `เมื่อพูดถึง <strong>${heading}</strong> สำหรับ ${docLabel} ที่จะยื่น${label} มีรายละเอียดสำคัญที่คนทั่วไปมักตกหล่น — เนื้อหานี้ครอบคลุมครบ`,
    `${heading} — คู่มือฉบับจริงจากทีมงานที่ยื่น${label}เป็นประจำ`,
  ]));

  const factsPool = [
    `ระบบรับรองผู้แปลแตกต่างกันตามประเทศ ต้องตรวจรายชื่อหรือคุณสมบัติจากสถานทูตและหน่วยงานผู้รับ`,
    `ตราประทับต้องมีชื่อผู้แปล · เลขทะเบียน · ภาษาที่ได้รับอนุญาต · ลายเซ็นสด (ไม่ใช่สแกน)`,
    `ลำดับการรับรองอาจมีการแปล กรมการกงสุล และสถานทูต แต่ต้องยืนยันว่าขั้นใดจำเป็นก่อนดำเนินการ`,
    `สถานที่ยื่นและระยะเวลาดำเนินการขึ้นกับบริการของหน่วยงานและระบบนัดหมายในช่วงนั้น`,
    `Schengen บางประเทศต้องการ Apostille — แต่ประเทศไทยไม่ใช่สมาชิก Hague จึงต้องใช้ chain legalization แทน`,
    `ชื่อในคำแปลต้องสะกดตรงกับพาสปอร์ต character-by-character — ผิดตัวเดียวสถานทูต reject`,
    `อายุเอกสารและวันที่รับรองต้องยึดตาม visa checklist ของสถานทูตปลายทาง`,
    `เอกสารบางประเภทอาจต้องออกใหม่หรือคัดสำเนาล่าสุด ให้ตรวจเงื่อนไขของหน่วยงานผู้รับก่อน`,
  ];
  paragraphs.push(`<ul>${shuffle(rng, factsPool).slice(0, 5).map((f) => `<li>${f}</li>`).join("")}</ul>`);

  if (embassy) {
    paragraphs.push(`<p><strong>${embassy.nameTh}</strong> — ภาษาปลายทาง: ${embassy.language} (${embassy.langCode}); ประเภทวีซ่าที่พบบ่อย: ${embassy.visaTypes.join(", ")}; เอกสารหลักที่ยื่น: ${embassy.keyDocs.join(", ")}</p>`);
  }
  if (doc) {
    paragraphs.push(`<p><strong>${doc.nameTh} (${doc.nameEn})</strong> — หมวด: ${doc.category} · ค่าบริการ: สอบถามเจ้าหน้าที่เพื่อขอใบเสนอราคา</p>`);
  }
  paragraphs.push(pick(rng, [
    `<p>คำแนะนำ: จองคิว MFA + Embassy ล่วงหน้าอย่างน้อย 1 เดือน — ช่วง peak season (มิ.ย.-ก.ย.) คิวยาวมาก</p>`,
    `<p>คำแนะนำ: ขอต้นฉบับใหม่จากอำเภอก่อนแปล — เอกสารเก่า 3+ เดือนสถานทูตหลายแห่งไม่รับ</p>`,
    `<p>คำแนะนำ: ถ้ามีเอกสารหลายฉบับในชุด (สูติบัตร + ทะเบียนบ้าน + ใบโสด) ทำพร้อมกันประหยัดค่าเดินเรื่องได้ 30-40%</p>`,
  ]));
  return paragraphs.join("\n");
}

export function generateSwornPage(ctx: SwornPageContext): SwornPageContent {
  const { embassy, doc, locale } = ctx;
  const seed = `sworn|${embassy?.slug ?? "pillar"}|${doc?.slug ?? "any"}|${locale}`;
  const rng = makeRng(seed);
  const angle = pick(rng, HERO_ANGLES);
  const h1 = embassy && doc
    ? `แปล ${doc.nameTh} ยื่น ${embassy.nameTh} — ${angle}`
    : embassy
      ? `นักแปลสาบานตน ${embassy.nameTh} — ${angle}`
      : doc
        ? `แปล ${doc.nameTh} โดยนักแปลสาบานตน — ${angle}`
        : `บริการนักแปลสาบานตนประจำสถานทูต (Sworn Translator) — ${angle}`;

  const priceLine = doc
    ? `ยืนยันราคาและกำหนดส่งหลังตรวจภาษา จำนวนหน้า และรูปแบบคำรับรอง`
    : `ยืนยันขอบเขต ราคา และกำหนดส่งหลังตรวจเอกสารและขั้นตอน legalization`;

  const intro = [
    `Thai Notary Law ให้บริการประสานงานคำแปลรับรอง โดยตรวจคุณสมบัติผู้แปลและรูปแบบเอกสารตามประเทศ สถานทูต และหน่วยงานผู้รับเป็นรายกรณี`,
    `หน้านี้เน้น${embassy ? ` <strong>${embassy.nameTh}</strong>` : "บริการนักแปลสาบานตน"}${doc ? ` สำหรับเอกสาร <strong>${doc.nameTh}</strong>` : ""} — ${priceLine}`,
    pick(rng, TRUST_HOOKS),
    `ตรวจชื่อ เลขเอกสาร วันที่ ภาษา และ checklist ทางการที่ใช้กับคำขอก่อนส่งมอบ`,
  ].join(" ");

  const chosenH2s = shuffle(rng, H2_POOL).slice(0, 8);
  const sections: SwornSection[] = chosenH2s.map((heading) => ({
    heading, html: buildSectionHtml(heading, ctx, rng),
  }));
  sections.push({
    heading: "ตัวอย่างสถานการณ์สำหรับเตรียมคำแปลยื่นวีซ่า",
    html: `<blockquote class="border-l-4 border-blue-600 bg-blue-50 p-4 italic">${pick(rng, CASE_STUDIES)}</blockquote>`,
  });

  const steps = shuffle(rng, STEP_POOL).slice(0, 8).map((s) => ({ tag: s.tag, text: s.th }));
  const faqs = shuffle(rng, FAQ_POOL).slice(0, 8);
  const benefits = shuffle(rng, BENEFIT_ROWS).slice(0, 6);

  const wordCount =
    countWords(h1) + countWords(intro) +
    sections.reduce((n, s) => n + countWords(s.heading) + countWords(s.html), 0) +
    faqs.reduce((n, f) => n + countWords(f.q) + countWords(f.a), 0) +
    steps.reduce((n, s) => n + countWords(s.text), 0);

  return { h1, intro, sections, faqs, steps, benefits, wordCount };
}
